import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  AttachMoney as AttachMoneyIcon,
  Security as SecurityIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from 'services/api';

const PolicyTerms = () => {
  const [policyTerms, setPolicyTerms] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [termsResponse, discountResponse] = await Promise.all([
        api.get('/api/v1/policy-terms'),
        api.get('/api/v1/discount-loading')
      ]);

      if (termsResponse.data.success) {
        setPolicyTerms(termsResponse.data.data);
      }

      if (discountResponse.data.success) {
        setDiscountLoading(discountResponse.data.data);
      }
    } catch (err) {
      console.error('Error loading policy terms:', err);
      setError('Failed to load policy terms and conditions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" alignItems="center" mb={2}>
          <DescriptionIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" color="white" fontWeight="bold">
              Policy Terms & Conditions
            </Typography>
            <Typography variant="h6" color="white" sx={{ opacity: 0.9 }}>
              Complete guide to shrimp insurance policy terms, eligibility, and coverage
            </Typography>
          </Box>
        </Box>
      </Paper>

      {policyTerms && (
        <>
          {/* Eligibility Requirements */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom color="primary" fontWeight="bold">
              <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Eligibility Requirements
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Requirements for shrimp insurance policy eligibility
            </Typography>
            
            <List>
              {policyTerms.eligibility.requirements.map((requirement, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={requirement} />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Policy Period & Coverage */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom color="primary" fontWeight="bold">
              <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Policy Period & Coverage
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Coverage periods and extended coverage options
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Species</strong></TableCell>
                        <TableCell><strong>Max DOC</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(policyTerms.policy_period.coverage_periods).map(([species, info]) => (
                        <TableRow key={species}>
                          <TableCell>
                            <Chip 
                              label={species.replace('_', ' ')} 
                              color="primary" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${info.max_doc} days`} 
                              color="secondary" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{info.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={4}>
                <Alert severity="info" sx={{ height: 'fit-content' }}>
                  <Typography variant="h6" gutterBottom>
                    Extended Coverage
                  </Typography>
                  <Typography variant="body2">
                    {policyTerms.policy_period.extended_coverage}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Paper>

          {/* Sum Insured Basis */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom color="primary" fontWeight="bold">
              <AttachMoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Sum Insured Basis
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {policyTerms.sum_insured.description}
            </Typography>
            
            <Grid container spacing={2}>
              {policyTerms.sum_insured.includes.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {item}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Coverage Options */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom color="primary" fontWeight="bold">
              <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Coverage Options
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Available insurance plans and their coverage
            </Typography>
            
            <Grid container spacing={3}>
              {Object.entries(policyTerms.coverage_options.plans).map(([planKey, plan]) => (
                <Grid item xs={12} md={4} key={planKey}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      background: planKey === 'plan_a' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
                                  planKey === 'plan_b' ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' :
                                  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white'
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                        {plan.coverage}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {plan.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Claims Procedure */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom color="primary" fontWeight="bold">
              <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Claims Procedure
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              How claims are processed and paid
            </Typography>
            
            <List>
              {policyTerms.claims_procedure.steps.map((step, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {index + 1}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={step} />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Exclusions */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom color="error" fontWeight="bold">
              <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Exclusions & Limitations
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Situations and conditions not covered by the insurance policy
            </Typography>
            
            <List>
              {policyTerms.exclusions.exclusions.map((exclusion, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CloseIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary={exclusion} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </>
      )}

      {/* Discount and Loading Information */}
      {discountLoading && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom color="primary" fontWeight="bold">
            <AttachMoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Discount & Loading Criteria
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Available discounts and applicable loadings for premium calculation
          </Typography>
          
          <Grid container spacing={3}>
            {/* Discounts */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="success.main" fontWeight="bold">
                    Available Discounts
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {discountLoading.discounts.description}
                  </Typography>
                  
                  <List dense>
                    {discountLoading.discounts.criteria.map((criterion, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2">
                                {criterion.name}
                              </Typography>
                              <Chip 
                                label={criterion.percentage} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={criterion.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Loadings */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="warning.main" fontWeight="bold">
                    Applicable Loadings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {discountLoading.loadings.description}
                  </Typography>
                  
                  <List dense>
                    {discountLoading.loadings.criteria.map((criterion, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2">
                                {criterion.name}
                              </Typography>
                              <Chip 
                                label={criterion.percentage} 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={criterion.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default PolicyTerms;
